import { Directive, ElementRef, Input, OnChanges, OnDestroy, afterNextRender, inject, numberAttribute } from '@angular/core';
import { gsap } from '../gsap';
import { QUERIES } from '../motion-env';
import { MotionService } from '../motion.service';

/**
 * Counts a number up from 0 the first time it gets a value. The directive owns the host's
 * content: a visually hidden span always holds the final value for screen readers, and an
 * aria-hidden span shows the digits while they count. Under reduced motion, on a language
 * switch and for every later change the final value is written at once.
 */
@Directive({ selector: '[appCountUp]' })
export class CountUpDirective implements OnChanges, OnDestroy {
  @Input('appCountUp') value: number | null | undefined = null;
  @Input({ transform: numberAttribute }) countUpDuration = 0.9;
  @Input({ transform: numberAttribute }) countUpDelay = 0;

  private readonly el: HTMLElement = inject(ElementRef).nativeElement;
  private readonly motion = inject(MotionService);
  private srEl?: HTMLSpanElement;
  private digitsEl?: HTMLSpanElement;
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private ready = false;
  /** The count-up has been started once (it never replays). */
  private started = false;
  private finished = false;

  constructor() {
    this.el.classList.add('dv-countup');
    afterNextRender(() => {
      this.ready = true;
      this.motion.outside(() => this.render());
    });
  }

  ngOnChanges(): void {
    // Before the first render the final value is written as-is, so the number is never
    // missing; the count-up itself starts in afterNextRender, before the first paint.
    if (this.ready) this.motion.outside(() => this.render());
    else this.writeFinal(this.current());
  }

  ngOnDestroy(): void {
    this.mm?.revert();
    this.mm = undefined;
  }

  private render(): void {
    const value = this.current();
    if (value === null || this.started) {
      this.mm?.revert();
      this.mm = undefined;
      this.writeFinal(value);
      return;
    }

    this.started = true;
    const mm = (this.mm = gsap.matchMedia(this.el));
    mm.add({ motion: QUERIES.motion }, (ctx) => {
      if (!ctx.conditions!['motion'] || this.finished || value === 0 || this.motion.recentLanguageSwitch()) {
        // Once the final value has been shown it stays; a later media flip never replays the count.
        this.finished = true;
        this.writeFinal(value);
        return;
      }
      const counter = { v: 0 };
      this.write(value, 0);
      gsap.to(counter, {
        v: value,
        duration: this.countUpDuration,
        delay: this.countUpDelay,
        ease: 'power2.out',
        snap: { v: 1 },
        onUpdate: () => this.writeDigits(counter.v),
        onComplete: () => {
          this.finished = true;
          this.writeFinal(value);
        },
      });
      // A revert (destroy or a reduced-motion flip) always lands on the final value.
      return () => this.writeFinal(value);
    });
  }

  private current(): number | null {
    const value = this.value;
    return value == null || !Number.isFinite(value) ? null : value;
  }

  /** Plain text once the count is over, so copying or reading the page gives the number once. */
  private writeFinal(value: number | null): void {
    this.srEl = this.digitsEl = undefined;
    this.el.textContent = value === null ? '' : String(value);
  }

  /** While counting: the final value for screen readers, `shown` in the aria-hidden digits. */
  private write(value: number | null, shown: number | null = value): void {
    if (value === null) {
      this.srEl = this.digitsEl = undefined;
      this.el.textContent = '';
      return;
    }
    if (!this.srEl || !this.digitsEl || this.srEl.parentNode !== this.el) {
      const doc = this.el.ownerDocument;
      this.srEl = doc.createElement('span');
      this.srEl.className = 'dv-sr-only';
      this.digitsEl = doc.createElement('span');
      this.digitsEl.setAttribute('aria-hidden', 'true');
      this.el.replaceChildren(this.srEl, this.digitsEl);
    }
    this.srEl.textContent = String(value);
    this.writeDigits(shown ?? value);
  }

  private writeDigits(n: number): void {
    if (this.digitsEl) this.digitsEl.textContent = String(Math.round(n));
  }
}
